// ============================================================
// src/components/layout/Layout.js
//
// ── REQUESTS NAV CHANGE ───────────────────────────────────────
// Requests is no longer a sidebar or bottom-nav item.
// It is accessible only via the bell icon in the persistent
// top bar that sits above all page content. The bell shows
// the live pending-count badge from PendingRequestsContext
// and links to /requests from every page.
// ============================================================

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Layout.css';
import { usePendingRequests } from '../../context/PendingRequestsContext';

function Layout({ children }) {

  const location  = useLocation()
  const navigate  = useNavigate()
  const { total: pendingTotal } = usePendingRequests()

  const isActive = (path) => (
    location.pathname === path || location.pathname.startsWith(path)
  )

  const isDashboardActive = (
    isActive('/')          &&
    !isActive('/gigs')     &&
    !isActive('/earnings') &&
    !isActive('/calendar') &&
    !isActive('/contacts') &&
    !isActive('/photos')   &&
    !isActive('/music')    &&
    !isActive('/travel')   &&
    !isActive('/promo')    &&
    !isActive('/requests') &&
    !isActive('/links')    &&
    !isActive('/settings')
  )

  return (
    <div className="app-container">

      {/* ── DESKTOP SIDEBAR ── */}
      <aside className="sidebar">

        <div className="sidebar-logo">
          <img
            src="/image/in-app-logo.png"
            alt="Performer Pocket Planner"
            className="sidebar-logo-img"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.parentElement.innerHTML =
                '<div style="font-family:var(--font-display);font-size:var(--text-2xl);color:var(--brand-primary);text-align:center;">Performer Pocket Planner</div>'
            }}
          />
        </div>

        <nav className="sidebar-nav">

          <Link to="/" className={`nav-link ${isDashboardActive ? 'active' : ''}`}>
            <i className="bi bi-house-fill nav-icon"></i>
            <span className="nav-label">Dashboard</span>
          </Link>

          <Link to="/gigs" className={`nav-link ${isActive('/gigs') ? 'active' : ''}`}>
            <i className="bi bi-star-fill nav-icon"></i>
            <span className="nav-label">Gigs</span>
          </Link>

          <Link to="/music" className={`nav-link ${isActive('/music') ? 'active' : ''}`}>
            <i className="bi bi-music-note-beamed nav-icon"></i>
            <span className="nav-label">Music</span>
          </Link>

          <Link to="/calendar" className={`nav-link ${isActive('/calendar') ? 'active' : ''}`}>
            <i className="bi bi-calendar-fill nav-icon"></i>
            <span className="nav-label">Calendar</span>
          </Link>

          <Link to="/contacts" className={`nav-link ${isActive('/contacts') ? 'active' : ''}`}>
            <i className="bi bi-people-fill nav-icon"></i>
            <span className="nav-label">Contacts</span>
          </Link>

          <Link to="/photos" className={`nav-link ${isActive('/photos') ? 'active' : ''}`}>
            <i className="bi bi-camera-fill nav-icon"></i>
            <span className="nav-label">Photos</span>
          </Link>

          <Link to="/earnings" className={`nav-link ${isActive('/earnings') ? 'active' : ''}`}>
            <i className="bi bi-cash-stack nav-icon"></i>
            <span className="nav-label">Earnings</span>
          </Link>

          <Link to="/travel" className={`nav-link ${isActive('/travel') ? 'active' : ''}`}>
            <i className="bi bi-luggage-fill nav-icon"></i>
            <span className="nav-label">Travel List</span>
          </Link>

          {/* Requests removed from sidebar — accessible via bell in top bar only */}

          <Link to="/promo" className={`nav-link ${isActive('/promo') ? 'active' : ''}`}>
            <i className="bi bi-image nav-icon"></i>
            <span className="nav-label">Show Promo Generator</span>
          </Link>

          <Link to="/links" className={`nav-link ${isActive('/links') ? 'active' : ''}`}>
            <i className="bi bi-link-45deg nav-icon"></i>
            <span className="nav-label">Fan Links</span>
          </Link>

          <Link to="/settings" className={`nav-link ${isActive('/settings') ? 'active' : ''}`}>
            <i className="bi bi-gear-fill nav-icon"></i>
            <span className="nav-label">Settings</span>
          </Link>

        </nav>
      </aside>


      {/* ── MAIN CONTENT ── */}
      <main className="main-content">

        {/* Mobile logo header — only visible on screens ≤ 768px */}
        <div className="mobile-header">
          <img
            src="/image/in-app-logo.png"
            alt="Performer Pocket Planner"
            className="mobile-logo"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.parentElement.innerHTML =
                '<div style="font-family:var(--font-display);font-size:var(--text-xl);color:var(--brand-primary);">Performer Pocket Planner</div>'
            }}
          />
        </div>

        {/* ── PERSISTENT BELL / TOP BAR ──────────────────────────────
            Sits above all page content on every route. The bell links
            to /requests and shows the live pending-count badge.
            On desktop it aligns to the right inside the content area.
            On mobile it appears below the logo header as a slim bar.
            ─────────────────────────────────────────────────────────── */}
        <div className="main-top-bar">
          <button
            className={`top-bar-bell ${isActive('/requests') ? 'top-bar-bell--active' : ''}`}
            onClick={() => navigate('/requests')}
            title={
              pendingTotal > 0
                ? `${pendingTotal} pending show request${pendingTotal !== 1 ? 's' : ''}`
                : 'Show Requests'
            }
            aria-label="Show Requests"
          >
            {/* Bell icon — filled when pending requests exist */}
            <i className={`bi ${pendingTotal > 0 ? 'bi-bell-fill' : 'bi-bell'}`}></i>

            {/* Floating count badge */}
            {pendingTotal > 0 && (
              <span className="top-bar-bell__badge">
                {pendingTotal > 9 ? '9+' : pendingTotal}
              </span>
            )}
          </button>
        </div>

        {children}
      </main>


      {/* ── MOBILE BOTTOM NAV ── */}
      <nav className="bottom-nav">

        <Link to="/" className={`bottom-nav-link ${isDashboardActive ? 'active' : ''}`}>
          <i className="bi bi-house-fill nav-icon"></i>
          <span>Home</span>
        </Link>

        <Link to="/gigs" className={`bottom-nav-link ${isActive('/gigs') ? 'active' : ''}`}>
          <i className="bi bi-star-fill nav-icon"></i>
          <span>Gigs</span>
        </Link>

        <Link to="/music" className={`bottom-nav-link ${isActive('/music') ? 'active' : ''}`}>
          <i className="bi bi-music-note-beamed nav-icon"></i>
          <span>Music</span>
        </Link>

        <Link to="/calendar" className={`bottom-nav-link ${isActive('/calendar') ? 'active' : ''}`}>
          <i className="bi bi-calendar-fill nav-icon"></i>
          <span>Calendar</span>
        </Link>

        <Link to="/contacts" className={`bottom-nav-link ${isActive('/contacts') ? 'active' : ''}`}>
          <i className="bi bi-people-fill nav-icon"></i>
          <span>Contacts</span>
        </Link>

        <Link to="/photos" className={`bottom-nav-link ${isActive('/photos') ? 'active' : ''}`}>
          <i className="bi bi-camera-fill nav-icon"></i>
          <span>Photos</span>
        </Link>

        <Link to="/earnings" className={`bottom-nav-link ${isActive('/earnings') ? 'active' : ''}`}>
          <i className="bi bi-cash-stack nav-icon"></i>
          <span>Earnings</span>
        </Link>

        <Link to="/travel" className={`bottom-nav-link ${isActive('/travel') ? 'active' : ''}`}>
          <i className="bi bi-luggage-fill nav-icon"></i>
          <span>Travel</span>
        </Link>

        {/* Requests removed from bottom nav — accessible via bell in top bar only */}

        <Link to="/promo" className={`bottom-nav-link ${isActive('/promo') ? 'active' : ''}`}>
          <i className="bi bi-image nav-icon"></i>
          <span>Show Promo</span>
        </Link>

        <Link to="/links" className={`bottom-nav-link ${isActive('/links') ? 'active' : ''}`}>
          <i className="bi bi-link-45deg nav-icon"></i>
          <span>Fan Links</span>
        </Link>

        <Link to="/settings" className={`bottom-nav-link ${isActive('/settings') ? 'active' : ''}`}>
          <i className="bi bi-gear-fill nav-icon"></i>
          <span>Settings</span>
        </Link>

      </nav>
    </div>
  )
}

export default Layout